import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import PageLoader from '../components/common/PageLoader'
import {
  isRegistrationOnlyMode,
  getRegistrationPath
} from '@/utils/registrationMode'
import { useEffect } from 'react'

// Lazy load all page components
const PublicLayout = lazy(() => import('../layouts/PublicLayout'))
const ProtectedLayout = lazy(
  () => import('../components/layouts/ProtectedLayout')
)
const LoginPage = lazy(() =>
  import('../pages/auth/LoginPage').then((module) => ({
    default: module.LoginPage
  }))
)
const RegistrationNPSN = lazy(() =>
  import('../pages/auth/RegistrationNPSN').then((module) => ({
    default: module.RegistrationNPSN
  }))
)
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'))
const JaringanSekolahPage = lazy(
  () => import('../pages/jaringan-sekolah/JaringanSekolahPage')
)
const JaringanSekolahUploadPage = lazy(
  () => import('../pages/jaringan-sekolah/JaringanSekolahUploadPage')
)
const SinkronisasiSekolahPage = lazy(
  () => import('../pages/jaringan-sekolah/sync/SinkronisasiSekolahPage')
)
const RingkasanWilayahPage = lazy(
  () => import('../pages/ringkasan-wilayah/RingkasanWilayahPage')
)
const BeritaPage = lazy(() => import('../pages/berita/BeritaPage'))
const AgendaPage = lazy(() => import('../pages/agenda/AgendaPage'))
const AksesAkunPage = lazy(() => import('../pages/akses-akun/AksesAkunPage'))
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((module) => ({
    default: module.NotFoundPage
  }))
)
const ThemeDemoPage = lazy(() =>
  import('../pages/theme-demo/ThemeDemoPage').then((module) => ({
    default: module.ThemeDemoPage
  }))
)
const SentryTestPage = lazy(() => import('../pages/SentryTestPage'))

// Wrapper component for lazy loading with fallback
const LazyWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Suspense fallback={<PageLoader loading={true} />}>{children}</Suspense>
  )
}

// Component to handle registration-only mode redirection
const RegistrationOnlyGuard = ({ children }: { children: React.ReactNode }) => {
  const registrationPath = getRegistrationPath()
  const currentPath = window.location.pathname

  useEffect(() => {
    if (isRegistrationOnlyMode() && currentPath !== registrationPath) {
      window.location.replace(registrationPath)
    }
  }, [currentPath, registrationPath])

  // If in registration-only mode and not on registration page, show loader while redirecting
  if (isRegistrationOnlyMode() && currentPath !== registrationPath) {
    return <PageLoader loading={true} />
  }

  return <>{children}</>
}

export const router = createBrowserRouter([
  // Public routes (login, register, etc.)
  {
    path: '/',
    element: (
      <RegistrationOnlyGuard>
        <LazyWrapper>
          <PublicLayout />
        </LazyWrapper>
      </RegistrationOnlyGuard>
    ),
    errorElement: (
      <LazyWrapper>
        <NotFoundPage />
      </LazyWrapper>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/login" replace />
      },
      {
        path: 'login',
        element: (
          <LazyWrapper>
            <LoginPage />
          </LazyWrapper>
        )
      },
      {
        path: 'registration-npsn',
        element: (
          <LazyWrapper>
            <RegistrationNPSN />
          </LazyWrapper>
        )
      },
      {
        path: 'theme-demo',
        element: (
          <LazyWrapper>
            <ThemeDemoPage />
          </LazyWrapper>
        )
      }
    ]
  },
  // Protected routes (dashboard, profile, etc.)
  {
    path: '/',
    element: (
      <RegistrationOnlyGuard>
        <LazyWrapper>
          <ProtectedLayout />
        </LazyWrapper>
      </RegistrationOnlyGuard>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <LazyWrapper>
            <DashboardPage />
          </LazyWrapper>
        )
      },
      {
        path: 'jaringan-sekolah',
        element: (
          <LazyWrapper>
            <JaringanSekolahPage />
          </LazyWrapper>
        )
      },
      {
        path: 'jaringan-sekolah/upload',
        element: (
          <LazyWrapper>
            <JaringanSekolahUploadPage />
          </LazyWrapper>
        )
      },
      {
        path: 'jaringan-sekolah/sinkronisasi',
        element: (
          <LazyWrapper>
            <SinkronisasiSekolahPage />
          </LazyWrapper>
        )
      },
      {
        path: 'ringkasan-wilayah',
        element: (
          <LazyWrapper>
            <RingkasanWilayahPage />
          </LazyWrapper>
        )
      },
      {
        path: 'berita',
        element: (
          <LazyWrapper>
            <BeritaPage />
          </LazyWrapper>
        )
      },
      {
        path: 'agenda',
        element: (
          <LazyWrapper>
            <AgendaPage />
          </LazyWrapper>
        )
      },
      {
        path: 'akses-akun',
        element: (
          <LazyWrapper>
            <AksesAkunPage />
          </LazyWrapper>
        )
      },
      {
        path: 'profile',
        element: (
          <LazyWrapper>
            <div className="p-6">Profile Page Coming Soon</div>
          </LazyWrapper>
        )
      },
      {
        path: 'settings',
        element: (
          <LazyWrapper>
            <div className="p-6">Settings Page Coming Soon</div>
          </LazyWrapper>
        )
      },
      {
        path: 'sentry-test',
        element: (
          <LazyWrapper>
            <SentryTestPage />
          </LazyWrapper>
        )
      }
    ]
  },
  // Fallback route
  {
    path: '*',
    element: (
      <LazyWrapper>
        <NotFoundPage />
      </LazyWrapper>
    )
  }
])
